/**
 * TaskFlowWbsView.tsx
 *
 * Work Breakdown Structure (WBS) Tree View with Roll-Up Progress & Estimates.
 * Displays collapsible multi-tier hierarchy with roll-up indicators and inline status edits.
 */

import React, { useState, useMemo } from 'react';
import type { TaskFlowItem, TaskFlowItemType } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import { WbsEngine, type WbsNode } from '../../../plugins/taskflow/engine/taskflow-wbs-engine.js';
import { globalTaskFlowStore } from '../../../plugins/taskflow/engine/taskflow-store.js';

export interface TaskFlowWbsViewProps {
  workspaceId: string;
  items: TaskFlowItem[];
  onSelectTask: (task: TaskFlowItem) => void;
  selectedTaskId?: string | null;
}

const TYPE_ICONS: Record<TaskFlowItemType, string> = {
  goal: '🎯',
  plan: '🗺️',
  project: '📦',
  task: '✅',
  action: '⚡',
};

export const TaskFlowWbsView: React.FC<TaskFlowWbsViewProps> = ({
  workspaceId,
  items,
  onSelectTask,
  selectedTaskId,
}) => {
  const [collapsedSet, setCollapsedSet] = useState<Set<string>>(new Set());

  const tree = useMemo(() => WbsEngine.buildTree(items), [items]);
  const flatNodes = useMemo(() => WbsEngine.flattenTree(tree, collapsedSet), [tree, collapsedSet]);

  const toggleCollapse = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStatusChange = (id: string, status: string, e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    globalTaskFlowStore.updateItem(id, {
      patch: { status },
      actorId: 'user',
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        overflow: 'auto',
      }}
    >
      {/* Table Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(300px, 2fr) 100px 100px 140px 100px 80px',
          padding: '12px 16px',
          backgroundColor: 'var(--theme-bg-header, rgba(15, 23, 42, 0.6))',
          borderBottom: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'var(--theme-text-secondary, #94a3b8)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div>Structure & Title</div>
        <div>Type</div>
        <div>Status</div>
        <div>Roll-Up Progress</div>
        <div>Roll-Up Est.</div>
        <div>Descendants</div>
      </div>

      {/* Tree Rows */}
      {flatNodes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--theme-text-muted, #64748b)' }}>
          No work items in tree.
        </div>
      ) : (
        flatNodes.map(({ node, isCollapsed, hasChildren }) => {
          const { item, level, rollupProgress, rollupEstimateHours, totalDescendants } = node;
          const isSelected = selectedTaskId === item.id;

          return (
            <div
              key={item.id}
              onClick={() => onSelectTask(item)}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(300px, 2fr) 100px 100px 140px 100px 80px',
                padding: '10px 16px',
                alignItems: 'center',
                borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.04))',
                backgroundColor: isSelected
                  ? 'var(--theme-accent-subtle, rgba(99, 102, 241, 0.15))'
                  : 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.03))';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Structure & Title Column with Indentation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: `${level * 24}px` }}>
                {hasChildren ? (
                  <button
                    onClick={(e) => toggleCollapse(item.id, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--theme-text-muted, #94a3b8)',
                      cursor: 'pointer',
                      fontSize: '10px',
                      padding: '2px 4px',
                      borderRadius: '4px',
                    }}
                  >
                    {isCollapsed ? '▶' : '▼'}
                  </button>
                ) : (
                  <span style={{ width: '16px', display: 'inline-block' }} />
                )}

                <span style={{ fontSize: '14px' }}>{TYPE_ICONS[item.type]}</span>
                <span style={{ fontWeight: 600, color: 'var(--theme-text-primary, #f8fafc)' }}>
                  {item.title}
                </span>
              </div>

              {/* Type Badge */}
              <div>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    color: 'var(--theme-accent, #818cf8)',
                    backgroundColor: 'var(--theme-accent-subtle, rgba(99, 102, 241, 0.12))',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {item.type}
                </span>
              </div>

              {/* Status Select */}
              <div>
                <select
                  value={item.status}
                  onChange={(e) => handleStatusChange(item.id, e.target.value, e)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.3))',
                    color: 'var(--theme-text-primary, #fff)',
                    border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    outline: 'none',
                  }}
                >
                  <option value="backlog">Backlog</option>
                  <option value="active">Active</option>
                  <option value="in-progress">In Progress</option>
                  <option value="blocked">Blocked</option>
                  <option value="review">Review</option>
                  <option value="done">Done</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Roll-up Progress Bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingRight: '16px' }}>
                <div
                  style={{
                    flex: 1,
                    height: '6px',
                    backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.1))',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${rollupProgress}%`,
                      height: '100%',
                      backgroundColor: rollupProgress === 100 ? 'var(--theme-status-success-text, #34d399)' : 'var(--theme-accent, #6366f1)',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-secondary, #94a3b8)', width: '30px' }}>
                  {rollupProgress}%
                </span>
              </div>

              {/* Roll-up Estimate */}
              <div style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #cbd5e1)' }}>
                {rollupEstimateHours > 0 ? `${rollupEstimateHours}h` : '—'}
              </div>

              {/* Descendants Count */}
              <div>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.08))',
                    color: 'var(--theme-text-muted, #94a3b8)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                  }}
                >
                  {totalDescendants}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
